import { FC, useMemo, useState } from 'react';

import { useRecoilValue } from 'recoil';

import { GiftItemInfoType } from '@shared/api/giftItemInfo/giftItemInfo.interface';
import {
  giftPriceTableDataAtom,
  giftPriceTableStatusAtom,
} from '@shared/api/priceTable/giftPriceTable.atom';
import { useGiftPriceTableHook } from '@shared/api/priceTable/giftPriceTable.hook';
import { Loading } from '@shared/components/Loading';
import PriceTableView from '@shared/components/PriceTableView';
import StatPageSideBar from '@shared/components/StatPageSideBar';
import { useAuth } from '@shared/state/hooks/useAuth';
import { agencyProfitLabel } from '@shared/utils/bugoBrandName.util';
import { Radio, Select } from 'antd';
import { useParams } from 'react-router-dom';

/** 배송형/쿠폰형 필터 ('all' = 전체) */
type GiftTypeFilter = 'all' | GiftItemInfoType;

/** 브랜드 필터 ('all' = 전체) */
type BrandFilter = 'all' | string;

/**
 * 답례품 단가표 페이지.
 * 현재 판매중인 답례품(giftItem)의 판매가·원가·상조 수익금을 보여준다.
 * 배송형/쿠폰형(giftItemInfo.type) 필터 제공, 원가율은 제외.
 */
export const AgencyAdminGiftPriceTablePage: FC = () => {
  const { bugoBrandId } = useParams();

  useGiftPriceTableHook(bugoBrandId);

  const status = useRecoilValue(giftPriceTableStatusAtom);
  const data = useRecoilValue(giftPriceTableDataAtom);

  //* 수익금 라벨은 보고 있는 상조의 약칭 — 특정 상조명 하드코딩 금지
  const { userBugoBrand } = useAuth();
  const profitLabel = agencyProfitLabel(userBugoBrand);

  const [typeFilter, setTypeFilter] = useState<GiftTypeFilter>('all');
  const [brandFilter, setBrandFilter] = useState<BrandFilter>('all');

  //* 데이터에 존재하는 브랜드 목록 (가나다순, 중복/공백 제거)
  const brandOptions = useMemo(() => {
    const set = new Set<string>();
    (data?.data ?? []).forEach((item) => {
      if (item.brandName) set.add(item.brandName);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b, 'ko'));
  }, [data]);

  const filteredItems = useMemo(() => {
    const items = data?.data ?? [];
    return items.filter(
      (item) =>
        (typeFilter === 'all' || item.type === typeFilter) &&
        (brandFilter === 'all' || item.brandName === brandFilter),
    );
  }, [data, typeFilter, brandFilter]);

  return (
    <div className="flex">
      <div className="flex flex-col">
        <StatPageSideBar />
      </div>
      <div className="mx-auto max-w-screen-2xl flex-1 px-10 py-8">
        <h1 className="text-2xl font-bold text-gray-900">답례품 단가표</h1>
        <p className="pt-2 pb-6 text-sm text-gray-500">
          현재 판매중인 답례품 상품의 판매가, 원가, {profitLabel}입니다.
        </p>
        {data ? (
          <div className="relative">
            {status !== 'success' && (
              <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/60">
                <Loading />
              </div>
            )}
            <div className="mb-4 flex flex-wrap items-center gap-3">
              <Radio.Group
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                optionType="button"
                buttonStyle="solid"
              >
                <Radio.Button value="all">전체</Radio.Button>
                <Radio.Button value={GiftItemInfoType.Delivery}>배송형</Radio.Button>
                <Radio.Button value={GiftItemInfoType.Coupon}>쿠폰형</Radio.Button>
              </Radio.Group>
              {brandOptions.length > 0 && (
                <Select<BrandFilter>
                  value={brandFilter}
                  onChange={setBrandFilter}
                  className="min-w-[160px]"
                  options={[
                    { value: 'all', label: '전체 브랜드' },
                    ...brandOptions.map((b) => ({ value: b, label: b })),
                  ]}
                />
              )}
            </div>
            <PriceTableView
              items={filteredItems}
              profitLabel={profitLabel}
              showCostRate={false}
              showGiftColumns
              feeRate={data.feeRate ?? null}
              tableName="답례품 단가표"
            />
          </div>
        ) : (
          <div className="center-box h-screen-15">
            <Loading />
          </div>
        )}
      </div>
    </div>
  );
};

export default AgencyAdminGiftPriceTablePage;
